/**
 * The Marketing-OS table engine's contracts (change `add-marketing-table-engine`).
 *
 * The engine renders layout and interaction — the frozen identity column, column tracks, group
 * sections, selection, the row-action slot, whole-row open, resize — and nothing else. Everything
 * with domain meaning (what a cell shows, what an identity looks like, which actions a row has)
 * arrives through these contracts from the consumer. Ids are opaque strings here; consumers keep
 * their own narrower unions at the definition site.
 */
import type { ComponentType, ReactNode } from 'react'

/** One configurable column's layout spec. Content comes from the consumer's `renderCell`. */
export interface IMarketingTableColumnSpec {
    id: string
    label: string
    /** Header type glyph ('Σ', '⟳', '#', …) — the column's provenance at a glance. */
    glyph?: string
    /** The glyph's hover explanation ("Calculated from reported metrics — read-only"). */
    glyphTitle?: string
    /** Track width in pixels, resize overlays already applied by the consumer. */
    width: number
    align: 'left' | 'right'
    /**
     * The server-side sort field this column discloses, or `null` when it cannot carry the
     * indicator. The header never sorts — it only reports which column the current order is on.
     */
    sortKey: string | null
    /**
     * An editable column's cells own their hover affordance, so the engine exempts them from the
     * row-hover backdrop (the same rule the creative table shipped with).
     */
    editable?: boolean
}

/** The pinned identity column's spec. Always present, always first, never in the configurable set. */
export interface IMarketingTableIdentitySpec {
    id: string
    label: string
    glyph?: string
    width: number
    sortKey: string | null
}

export interface IMarketingTableSortRule {
    field: string
    direction: 'asc' | 'desc'
}

/**
 * One group section, in the pre-order stream the consumer's backend already speaks: an inner node
 * (`hasChildGroups`) renders no row area — its children are the segment rows that FOLLOW it.
 */
export interface IMarketingTableSegment<TRow> {
    key: string
    label: string
    /** Richer rowgroup label for assistive tech ("Video — grouped by format, level 1"). */
    ariaLabel?: string
    /** Nesting level, expressed by indent rather than DOM nesting (paging can cut a subtree). */
    depth: number
    hasChildGroups: boolean
    /** The header's right-aligned counts line — "12 creatives · 34 ads". Consumer-formatted. */
    countsLabel: string
    /**
     * The group's aggregate as a row-shaped projection, rendered through the SAME `renderCell` the
     * child rows use (with `isAggregate` set) so a group's numbers can never be formatted
     * differently from its children. `null` renders em dashes.
     */
    aggregateRow: TRow | null
    rows: TRow[]
}

/** Context handed to `renderCell` — lets a consumer withhold editors/verdicts from aggregates. */
export interface IMarketingTableCellContext {
    isAggregate: boolean
}

/**
 * One row action for the ⋯ menu. Structurally compatible with the suite's `ICreativeAction`, so a
 * consumer's existing action model plugs in without an adapter.
 */
export interface IMarketingTableRowAction {
    key: string
    label: string
    Icon: ComponentType<{ className?: string }>
    onClick: () => void | Promise<void>
    disabled?: boolean
    /** Visual "on" state — e.g. Save shows as already saved. */
    active?: boolean
    /** Long-running work in flight. */
    busy?: boolean
    /** Renders in the red palette. */
    destructive?: boolean
    /**
     * When set, this action renders `customMenuItem` instead of a plain DropdownMenuItem —
     * used for cascading submenus (e.g. Set status) that own their own trigger chrome.
     */
    customMenuItem?: ReactNode
}

/** The controlled selection contract. Absent → the table renders no selection gutter at all. */
export interface IMarketingTableSelection {
    selectedKeys: ReadonlySet<string>
    onToggleRow: (key: string, options?: { shiftKey?: boolean }) => void
    /** Flip between all-selectable-loaded-rows and none. */
    onToggleAll: () => void
    /** Hover title on the header checkbox when not all-selected. */
    selectAllTitle?: string
    /**
     * Overrides the engine's loaded-rows header checkbox math. Grouped tables need this: "every
     * loaded row is selected" is NOT "the whole outline is selected" when other groups are still
     * collapsed — without an override the header paints a full check for a single open group.
     */
    headerState?: 'all' | 'mixed' | 'none'
    /**
     * Per-row eligibility (a draft that cannot be bundled, a row with nothing to act on). An
     * ineligible row keeps the reserved gutter — so identity content never shifts — but renders no
     * checkbox, and the header's all-selected math skips it. The consumer's `orderedKeys` for
     * select-all/ranges should apply the same filter. Absent → every row is selectable.
     */
    isRowSelectable?: (key: string) => boolean
}

/**
 * A body row is tall enough to carry a legible poster; header and group rows carry text only, so
 * they stay at the compact Linear height. The header height is also the sticky offset group rows
 * park against, which is why it is a constant and not a literal.
 */
export const MARKETING_TABLE_ROW_HEIGHT = 56
export const MARKETING_TABLE_HEADER_HEIGHT = 36

/**
 * Control column hit area (checkbox / group chevron) — both are `h-6 w-6`, so identity content
 * never shifts when the checkbox appears, and the checkbox lines up with the group chevron.
 */
export const MARKETING_TABLE_GUTTER_WIDTH = 24

/**
 * Reserved width for the hover ⋯ in the identity cell. Same as the menu trigger hit box — kept as
 * a constant so loading skeletons can park the thumbnail where the live row will put it.
 */
export const MARKETING_TABLE_ACTIONS_SLOT_WIDTH = 24

/** How far one group-nesting level indents inside the frozen identity cell. */
export const MARKETING_TABLE_GROUP_INDENT = 16

/**
 * Interactive descendants of a row that own their own click. A whole-row open must never fire for
 * these — editable cells, the checkbox, the row menu, resize handles and menus all match.
 * `[data-row-click-exempt]` is the opt-out for anything that doesn't.
 */
export const MARKETING_TABLE_ROW_CLICK_EXEMPT =
    'button, a, input, textarea, select, [role="button"], [role="separator"], [data-row-click-exempt]'

/** Whether a row click landed on (or inside) something that owns its own behaviour. */
export function shouldIgnoreMarketingTableRowClick(target: Element | null): boolean {
    // An unidentifiable target is ignored rather than opened — the row must never fire blind.
    if (!target) return true
    return Boolean(target.closest(MARKETING_TABLE_ROW_CLICK_EXEMPT))
}
